import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  getCurrentLocationPath,
  hasOAuthCallbackParams,
  useAlert,
  useAuth,
  useSessionStorage,
} from '@spotify/confidence-core-react';

import {
  CreateAccountFormContext,
  type HandleLoginOptions,
  type SignupScreen,
} from './CreateAccountFormContext';
import { getSignupAttribution } from './hooks/attribution';
import {
  type ToggleAuthConnectionOptions,
  useAuthConnections,
} from './hooks/useAuthConnections';
import { Region, useCreateAccount } from './hooks/useCreateAccount';
import { useEmailVerification } from './hooks/useEmailVerification';
import type { CreatedAccount, InviteResponse } from './hooks/useInvite';
import { useSignupTracking } from './hooks/useSignupTracking';
import { useWorkspaceNameFields } from './hooks/useWorkspaceNameFields';
import {
  DEFAULT_STATE,
  buildCreateAccountRequest,
  getAuthErrorCode,
  getEnabledAuthConnections,
  getSignupAuthErrorMessage,
  isAccountUnderReviewError,
  isAuthCancellationError,
  isEmailNotVerifiedError,
} from './signupUtils';
import type { AccountDetailsState } from './signupUtils';

export type { SignupScreen } from './CreateAccountFormContext';
export {
  useAccountDetailsContext,
  useAuthConnectionsContext,
  useCreateAccountFormContext,
  useWorkspaceNameFieldsContext,
} from './CreateAccountFormContext';

export type SignupInvite = Pick<InviteResponse, 'email' | 'autoJoinDomain'>;

type CreateAccountFormProviderProps = {
  inviteId?: string;
  invite?: SignupInvite;
  hasAuthCallback?: boolean;
  onCreate: (account?: CreatedAccount) => void;
  children: ReactNode;
};

const getAuthOutcome = (error: unknown): 'success' | 'error' | 'abandoned' => {
  if (isAuthCancellationError(error)) return 'abandoned';
  if (isEmailNotVerifiedError(error)) return 'success';
  return 'error';
};

export const CreateAccountFormProvider = ({
  inviteId,
  invite,
  hasAuthCallback,
  onCreate,
  children,
}: CreateAccountFormProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const alert = useAlert();

  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
    error: authError,
  } = useAuth();

  const [{ value: createdAccount }, createAccount] = useCreateAccount(inviteId);
  const [submissionError, setSubmissionError] = useState<Error>();
  const [showOverlay, setShowOverlay] = useState(false);
  const [suppressAuthAlert, setSuppressAuthAlert] = useSessionStorage(
    'signup-suppress-auth-alert',
    false,
    { migrateKey: false },
  );
  const authCallbackDetected =
    hasAuthCallback ?? hasOAuthCallbackParams(searchParams);

  const {
    trackStep,
    trackInteraction,
    trackLifecycle,
    beginAuthRedirect,
    completeAuthRedirect,
    hasReturnedAuthAttempt,
    trackCompletion,
    setOrgContext,
    getPreviousStepId,
  } = useSignupTracking({
    enabled: isAuthenticated,
    hasAuthCallback: authCallbackDetected,
  });

  const signupAttribution = useMemo(
    () => getSignupAttribution(searchParams),
    [searchParams],
  );

  const [acceptMarketingEmails, setAcceptMarketingEmails] = useState(true);

  const [accountDetails, setAccountDetails] =
    useSessionStorage<AccountDetailsState>(
      `signup-slim-v1-${inviteId ?? 'regular'}`,
      DEFAULT_STATE,
      { migrateKey: false },
    );

  useEffect(() => {
    if (createdAccount) {
      trackCompletion('success');
      setAccountDetails(DEFAULT_STATE);
      onCreate(createdAccount);
    }
  }, [createdAccount, onCreate, setAccountDetails, trackCompletion]);

  const handleResultDismissed = useCallback(() => {
    setSubmissionError(undefined);
    setShowOverlay(false);
  }, []);

  useEffect(() => {
    if (submissionError) {
      trackCompletion('error', {
        error_message: submissionError.message,
        final_step_id: 'workspace_setup',
      });
    }
  }, [submissionError, trackCompletion]);

  const setField = useCallback(
    (newData: Partial<AccountDetailsState>) => {
      setAccountDetails(current => ({ ...current, ...newData }));
    },
    [setAccountDetails],
  );

  useEffect(() => {
    if (!invite) return;
    setField({ loginDomain: invite.autoJoinDomain || undefined });
  }, [invite, setField]);

  useEffect(() => {
    if (!user?.email) return;
    setField({ adminEmail: user.email });
  }, [user?.email, setField]);

  const {
    toggleAuthConnection: applyAuthConnectionToggle,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
  } = useAuthConnections({
    user,
    inviteId,
    authConnections: accountDetails.authConnections,
    setAccountDetails,
  });

  const {
    loginId,
    handleDisplayNameChange: applyDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    validLoginId,
    helperText,
    loginIdAvailable,
    loginIdError,
    loginIdConfirmedAvailable,
    isCheckingAvailability,
  } = useWorkspaceNameFields({
    displayName: accountDetails.displayName,
    setField,
    inviteId,
  });

  useEffect(() => {
    setOrgContext({
      display_name: accountDetails.displayName || undefined,
      workspace_name: loginId || undefined,
    });
  }, [accountDetails.displayName, loginId, setOrgContext]);

  const hasTrackedNameInputRef = useRef(false);

  const handleDisplayNameChange = useCallback(
    (value: string) => {
      if (!hasTrackedNameInputRef.current && value.trim() !== '') {
        hasTrackedNameInputRef.current = true;
        trackInteraction({
          control_id: 'workspace_name',
          control_type: 'text_input',
        });
      }
      applyDisplayNameChange(value);
    },
    [applyDisplayNameChange, trackInteraction],
  );

  const handleRegionChange = useCallback(
    (region: Region) => {
      trackInteraction({
        control_id: 'storage_region',
        control_type: 'radio',
        value: region,
      });
      setField({ region });
    },
    [setField, trackInteraction],
  );

  const toggleAuthConnection = useCallback(
    ({ connection, checked }: ToggleAuthConnectionOptions) => {
      trackInteraction({
        control_id: 'auth_connection',
        control_type: 'toggle_checkbox',
        value: connection,
        checked,
      });
      applyAuthConnectionToggle({ connection, checked });
    },
    [applyAuthConnectionToggle, trackInteraction],
  );

  const handleAcceptMarketingEmailsChange = useCallback(
    (checked: boolean) => {
      trackInteraction({
        control_id: 'marketing_emails',
        control_type: 'toggle_checkbox',
        checked,
      });
      setAcceptMarketingEmails(checked);
    },
    [trackInteraction],
  );

  const hasCleanedStepParam = useRef(false);
  useEffect(() => {
    if (hasCleanedStepParam.current || !searchParams.has('step')) return;
    hasCleanedStepParam.current = true;
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('step');
    setSearchParams(cleaned, { replace: true });
  }, [searchParams, setSearchParams]);

  const isEmailVerified = user?.email_verified ?? false;
  const currentUserEmail = user?.email;
  const inviteEmail = invite?.email.trim() || undefined;

  const hasInviteEmailMismatch =
    invite !== undefined &&
    (!inviteEmail ||
      currentUserEmail?.toLowerCase() !== inviteEmail.toLowerCase());

  const deriveScreen = (): SignupScreen => {
    if (isLoading) return 'loading';
    if (isAuthenticated && hasInviteEmailMismatch) {
      return 'invite_email_mismatch';
    }
    if (isAuthenticated && !isEmailVerified) return 'verify_email';
    if (isAuthenticated && isAccountUnderReviewError(submissionError)) {
      return 'under_review';
    }
    if (isAuthenticated) return 'workspace_setup';
    if (isEmailNotVerifiedError(authError)) return 'login_verify_email';
    return 'welcome';
  };
  const screen = deriveScreen();

  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (screen !== 'workspace_setup' || hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    trackStep('impression', {
      step_id: 'workspace_setup',
      step_name: 'Workspace Setup',
      step_index: 0,
      step_count: 1,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  useEffect(() => {
    if (isLoading || !hasReturnedAuthAttempt) return;
    if (authError) {
      completeAuthRedirect(
        getAuthOutcome(authError),
        getAuthErrorCode(authError) ?? 'unknown',
      );
      setSuppressAuthAlert(false);
      return;
    }
    completeAuthRedirect(isAuthenticated ? 'success' : 'abandoned');
    setSuppressAuthAlert(false);
  }, [
    authError,
    completeAuthRedirect,
    hasReturnedAuthAttempt,
    isAuthenticated,
    isLoading,
    setSuppressAuthAlert,
  ]);

  const handledAuthErrorRef = useRef<Error>();
  useEffect(() => {
    if (!authError) {
      handledAuthErrorRef.current = undefined;
      return;
    }
    if (handledAuthErrorRef.current === authError) return;
    handledAuthErrorRef.current = authError;

    setSuppressAuthAlert(false);

    if (
      suppressAuthAlert ||
      isEmailNotVerifiedError(authError) ||
      isAuthCancellationError(authError)
    ) {
      return;
    }

    alert.post({
      message: getSignupAuthErrorMessage(authError),
      severity: 'error',
    });
  }, [alert, authError, setSuppressAuthAlert, suppressAuthAlert]);

  const handleLogin = useCallback(
    async (opts: HandleLoginOptions = {}) => {
      const { trigger = 'interaction', silent = false } = opts;

      setSuppressAuthAlert(silent);

      beginAuthRedirect({ trigger });

      try {
        await loginWithRedirect({
          authorizationParams: {
            ...(!inviteId ? { screen_hint: 'signup' } : {}),
          },
          appState: { returnTo: getCurrentLocationPath() },
        });
      } catch (loginError) {
        setSuppressAuthAlert(false);
        const isCancellation = isAuthCancellationError(loginError);
        const isEmailVerificationRequired = isEmailNotVerifiedError(loginError);
        completeAuthRedirect(
          getAuthOutcome(loginError),
          getAuthErrorCode(loginError) ?? 'unknown',
        );
        if (!silent && !isEmailVerificationRequired && !isCancellation) {
          alert.post({
            message: getSignupAuthErrorMessage(loginError),
            severity: 'error',
          });
        }
      }
    },
    [
      alert,
      beginAuthRedirect,
      completeAuthRedirect,
      inviteId,
      loginWithRedirect,
      setSuppressAuthAlert,
    ],
  );

  const autoLoginTriggeredRef = useRef(false);
  useEffect(() => {
    if (
      autoLoginTriggeredRef.current ||
      screen !== 'welcome' ||
      !searchParams.has('verified')
    ) {
      return;
    }

    autoLoginTriggeredRef.current = true;
    const params = new URLSearchParams(searchParams);
    params.delete('verified');

    setSearchParams(params, { replace: true });
    void handleLogin({ trigger: 'automatic', silent: true });
  }, [screen, searchParams, setSearchParams, handleLogin]);

  const handleLogout = useCallback(async (): Promise<void> => {
    const returnTo = new URL('/signup', window.location.origin);
    returnTo.searchParams.set(
      'signupReturnTo',
      `${window.location.pathname}${window.location.search}`,
    );
    try {
      await logout({
        logoutParams: {
          returnTo: returnTo.toString(),
        },
      });
    } catch {
      alert.post({
        message: "We couldn't sign you out. Try again.",
        severity: 'error',
      });
    }
  }, [alert, logout]);

  const canSubmit =
    !!loginId &&
    loginIdConfirmedAvailable &&
    validDisplayName &&
    validLoginId &&
    !!accountDetails.displayName &&
    (!showAuthConnections || hasSelectedAuthConnection) &&
    !showOverlay;

  const handleCreate = useCallback(async (): Promise<void> => {
    if (!user || !canSubmit) return;
    trackInteraction({
      control_id: 'create_workspace',
      control_type: 'button',
      form_state: {
        storage_region: accountDetails.region,
        marketing_emails: acceptMarketingEmails,
        auth_connections: getEnabledAuthConnections(
          accountDetails.authConnections,
        ),
      },
    });
    setSubmissionError(undefined);
    setShowOverlay(true);

    const createAccountResult = await createAccount(
      buildCreateAccountRequest({
        accountDetails,
        loginId,
        inviteId,
        marketingOptIn: acceptMarketingEmails,
        signupAttribution,
      }),
    );
    if (createAccountResult instanceof Error) {
      setSubmissionError(createAccountResult);
    }
  }, [
    user,
    canSubmit,
    accountDetails,
    loginId,
    inviteId,
    acceptMarketingEmails,
    signupAttribution,
    createAccount,
    trackInteraction,
  ]);

  const hasTrackedVerifyEmailRef = useRef(false);
  useEffect(() => {
    if (screen !== 'verify_email' || hasTrackedVerifyEmailRef.current) return;
    hasTrackedVerifyEmailRef.current = true;
    trackStep('impression', {
      step_id: 'verify_email',
      step_name: 'Verify Email',
      step_index: -1,
      step_count: 2,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const verificationTrackingStateRef = useRef<
    'unseen' | 'required' | 'succeeded'
  >('unseen');
  useEffect(() => {
    if (
      !['verify_email', 'login_verify_email'].includes(screen) ||
      verificationTrackingStateRef.current !== 'unseen'
    ) {
      return;
    }
    verificationTrackingStateRef.current = 'required';
    trackLifecycle({
      eventType: 'verification_required',
      trigger: 'impression',
    });
  }, [screen, trackLifecycle]);

  useEffect(() => {
    if (
      verificationTrackingStateRef.current !== 'required' ||
      !isEmailVerified
    ) {
      return;
    }
    verificationTrackingStateRef.current = 'succeeded';
    trackLifecycle({
      eventType: 'verification_succeeded',
      trigger: 'completion',
      outcome: { status: 'success' },
    });
  }, [isEmailVerified, trackLifecycle]);

  const { recheckEmailVerification, status: emailVerificationStatus } =
    useEmailVerification({
      getAccessTokenSilently,
      enabled: screen === 'verify_email',
    });

  useEffect(() => {
    if (emailVerificationStatus === 'pending' && !isEmailVerified) {
      trackLifecycle({
        eventType: 'verification_pending',
        trigger: 'completion',
        outcome: { status: 'pending' },
      });
    }
    if (emailVerificationStatus === 'error') {
      trackLifecycle({
        eventType: 'verification_check_failed',
        trigger: 'error',
        outcome: { status: 'error' },
      });
    }
    if (emailVerificationStatus === 'sign_in_required') {
      trackLifecycle({
        eventType: 'reauthentication_required',
        trigger: 'error',
        outcome: { status: 'required' },
      });
    }
  }, [emailVerificationStatus, isEmailVerified, trackLifecycle]);

  const contextValue = useMemo(
    () => ({
      accountDetails,
      setField,
      handleRegionChange,
      toggleAuthConnection,
      hasSelectedAuthConnection,
      showAuthConnections,
      userAuthConnection,
      loginId,
      handleDisplayNameChange,
      handleDisplayNameBlur,
      validDisplayName,
      validLoginId,
      helperText,
      loginIdAvailable,
      loginIdError,
      loginIdConfirmedAvailable,
      isCheckingAvailability,
      screen,
      error: submissionError,
      showOverlay,
      canSubmit,
      currentUserEmail,
      inviteEmail,
      emailVerificationStatus,
      acceptMarketingEmails,
      setAcceptMarketingEmails: handleAcceptMarketingEmailsChange,
      handleLogin,
      handleLogout,
      handleCreate,
      handleResultDismissed,
      recheckEmailVerification,
    }),
    [
      accountDetails,
      setField,
      handleRegionChange,
      toggleAuthConnection,
      hasSelectedAuthConnection,
      showAuthConnections,
      userAuthConnection,
      loginId,
      handleDisplayNameChange,
      handleDisplayNameBlur,
      validDisplayName,
      validLoginId,
      helperText,
      loginIdAvailable,
      loginIdError,
      loginIdConfirmedAvailable,
      isCheckingAvailability,
      screen,
      submissionError,
      showOverlay,
      canSubmit,
      currentUserEmail,
      inviteEmail,
      emailVerificationStatus,
      acceptMarketingEmails,
      handleAcceptMarketingEmailsChange,
      handleLogin,
      handleLogout,
      handleCreate,
      handleResultDismissed,
      recheckEmailVerification,
    ],
  );

  return (
    <CreateAccountFormContext.Provider value={contextValue}>
      {children}
    </CreateAccountFormContext.Provider>
  );
};
